import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Stethoscope, MessageSquare, Calendar, FileText } from 'lucide-react';

/**
 * HomeSystem — Section 2 / Home V2.
 *
 * Presents Prime Vision as a 4-pillar SYSTEM (not a feature list).
 * Each pillar has icon + title + body. Sober, medical, ample whitespace.
 */
const COPY = {
  it: {
    eyebrow: 'Il sistema Prime Vision',
    title_a: 'Un unico flusso clinico,',
    title_b: 'dal momento in cui il paziente entra.',
    lead:
      'Quattro pilastri che si parlano davvero fra loro. Nessuna doppia digitazione, nessun software in più da aprire.',
    pillars: [
      {
        icon: Stethoscope,
        title: 'Cartella clinica sartoriale',
        body: 'Pensata per la visita oculistica: anamnesi, terapie, storia clinica, follow-up. Tutto sotto mano, niente form generici.',
      },
      {
        icon: MessageSquare,
        title: 'AI vocale per il referto',
        body: 'Detti la visita, l\'AI struttura il referto. Il medico torna a guardare il paziente, non lo schermo.',
      },
      {
        icon: Calendar,
        title: 'Agenda smart e follow-up',
        body: 'Reminder automatici, recall pazienti dormienti, percorsi pre e post intervento. Il paziente non viene dimenticato.',
      },
      {
        icon: FileText,
        title: 'Fatture e documentazione FSE 2.0',
        body: 'Fatturazione elettronica integrata e supporto a un percorso FSE 2.0 a norma. Meno errori, meno carta.',
      },
    ],
  },
  en: {
    eyebrow: 'The Prime Vision system',
    title_a: 'One clinical flow,',
    title_b: 'from the moment the patient walks in.',
    lead:
      'Four pillars that actually talk to each other. No double entry, no extra software to open.',
    pillars: [
      {
        icon: Stethoscope,
        title: 'Tailored medical record',
        body: 'Designed for the ophthalmology visit: anamnesis, therapies, clinical history, follow-up. All at hand, no generic forms.',
      },
      {
        icon: MessageSquare,
        title: 'Voice AI for the report',
        body: 'You dictate the visit, the AI structures the report. The doctor goes back to looking at the patient, not the screen.',
      },
      {
        icon: Calendar,
        title: 'Smart agenda and follow-up',
        body: 'Automatic reminders, dormant-patient recalls, pre and post-op paths. The patient is never forgotten.',
      },
      {
        icon: FileText,
        title: 'Invoices and FSE 2.0 documentation',
        body: 'Built-in e-invoicing and support for a compliant FSE 2.0 process. Fewer errors, less paper.',
      },
    ],
  },
};

export const HomeSystem = () => {
  const { locale } = useLanguage();
  const c = COPY[locale] || COPY.it;

  return (
    <section
      data-testid="pv-home-system"
      className="relative py-20 md:py-28 border-t border-slate-100 overflow-hidden"
    >
      {/* Soft full-width medical blue band */}
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-[#F4F9FC] via-white to-white" />

      <div className="relative pv-container">
        <div className="max-w-3xl">
          <span className="text-[11px] md:text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            {c.eyebrow}
          </span>
          <h2 className="mt-5 font-heading font-bold tracking-normal text-[30px] sm:text-[38px] md:text-[44px] leading-[1.10] text-pv-ink">
            {c.title_a}
            <br />
            <span className="text-pv-teal">{c.title_b}</span>
          </h2>
          <p className="mt-5 md:mt-6 text-slate-600 text-[15.5px] md:text-[16.5px] leading-[1.7] max-w-2xl">
            {c.lead}
          </p>
        </div>

        <div className="mt-14 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {c.pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <article
                key={i}
                data-testid={`pv-home-system-pillar-${i + 1}`}
                className="relative bg-white rounded-2xl border border-slate-200 p-6 md:p-7 hover:border-pv-teal/35 transition-colors"
              >
                <div className="flex items-start gap-5">
                  <span className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-pv-ice border border-pv-teal/20">
                    <Icon className="w-5 h-5 text-pv-teal" />
                  </span>
                  <div className="flex-1">
                    <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase font-semibold text-slate-400">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <h3 className="mt-1 font-heading font-bold text-[18px] md:text-[20px] text-pv-ink leading-tight">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-[14.5px] md:text-[15px] text-slate-600 leading-relaxed">
                      {p.body}
                    </p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HomeSystem;
